import React from "react";
import { Step } from "react-joyride";

export const mainTourSteps: Step[] = [
	{
		target: "body",
		content: (
			<div>
				<h3>Welcome to ARTEH! 👋</h3>
				<p>
					Let's take a quick tour to help you get started with
					tracking and reducing your carbon emissions.
				</p>
				<p>
					<strong>Note:</strong> You're currently exploring the{" "}
					<strong>Demo Company</strong> — a fully populated dashboard
					with sample data, so you can see how everything works.
				</p>
				<p>
					💡 <strong>Tip:</strong> When you're ready to add real
					data, switch to your own company using the dropdown in the
					header.
				</p>
				<p>
					This tour will take 2 minutes and show you all the key
					features.
				</p>
			</div>
		),
		placement: "center",
		disableBeacon: true,
	},
	{
		target: ".company-selector",
		content: (
			<div>
				<h3>Company Switcher 🔄</h3>
				<p>
					<strong>This is the Company Selector!</strong> Use this
					dropdown to switch between companies.
				</p>
				<p>
					🏢 <strong>Your Company</strong>: Currently empty (no data
					uploaded yet)
				</p>
				<p>
					📊 <strong>Demo Company</strong>: Fully populated with
					sample emissions data
				</p>
				<p>
					<strong>Try it now!</strong> You're on the Demo Company at
					the moment — click this dropdown to switch to your own
					company whenever you're ready. You can switch back anytime.
				</p>
			</div>
		),
		placement: "bottom",
		spotlightClicks: true,
	},
	{
		target: ".menu-button",
		content: (
			<div>
				<h3>Navigation Menu</h3>
				<p>
					Click this menu button to access all features. We'll open it
					now to show you around!
				</p>
			</div>
		),
		placement: "bottom",
	},
	{
		target: ".dashboard-link",
		content: (
			<div>
				<h3>Dashboard</h3>
				<p>
					Your dashboard shows a complete view of your carbon
					footprint:
				</p>
				<ul style={{ paddingLeft: 20, margin: "10px 0" }}>
					<li>Emissions by scope (1, 2, and 3)</li>
					<li>Trends over time</li>
					<li>Breakdown by category</li>
				</ul>
				<p>📊 Click here to view your emissions dashboard!</p>
			</div>
		),
		placement: "right",
	},
	{
		target: ".upload-data-link",
		content: (
			<div>
				<h3>Upload Your Data</h3>
				<p>This is where you upload your emissions data:</p>
				<ul style={{ paddingLeft: 20, margin: "10px 0" }}>
					<li>Upload Excel or CSV files</li>
					<li>Use our templates</li>
					<li>Enter data manually</li>
				</ul>
				<p>
					💡 Start with electricity and fuel data for quick insights!
				</p>
				<p>
					<strong>
						Let's explore the upload page together - click Next to
						continue!
					</strong>
				</p>
			</div>
		),
		placement: "right",
	},
	{
		target: "body",
		content: (
			<div>
				<h3>Welcome to the Upload Page! 📁</h3>
				<p>
					<strong>Great! We're now on the file upload page.</strong>
				</p>
				<p>
					This is the main way to add your emissions data to ARTEH.
					Let's explore the three key elements:
				</p>
				<ul style={{ paddingLeft: 20, margin: "10px 0" }}>
					<li>📥 Where to download our Excel template</li>
					<li>📤 How to upload your files</li>
					<li>✅ What happens after you submit</li>
				</ul>
			</div>
		),
		placement: "center",
		disableBeacon: true,
	},
	{
		target: ".upload-template-link",
		content: (
			<div>
				<h3>Step 1: Download Template 📥</h3>
				<p>
					<strong>
						First, download our Excel template by clicking this
						link!
					</strong>
				</p>
				<p>
					The template makes data entry easy with pre-formatted sheets
					for different emission categories:
				</p>
				<ul style={{ paddingLeft: 20, margin: "10px 0" }}>
					<li>⚡ Electricity consumption</li>
					<li>🔥 Fuel combustion</li>
					<li>✈️ Business travel</li>
					<li>🚗 Company vehicles</li>
					<li>And more...</li>
				</ul>
				<p>
					Each sheet includes instructions, examples, and automatic
					calculations!
				</p>
			</div>
		),
		placement: "bottom",
	},
	{
		target: ".upload-dropzone-area",
		content: (
			<div>
				<h3>Step 2: Upload Your File 📤</h3>
				<p>
					<strong>
						Once you've filled in the template, upload it here!
					</strong>
				</p>
				<p>Two ways to upload:</p>
				<ul style={{ paddingLeft: 20, margin: "10px 0" }}>
					<li>🖱️ Click the area and browse for your file</li>
					<li>✋ Drag and drop your file directly here</li>
				</ul>
				<p>
					<strong>Supported formats:</strong>
				</p>
				<ul style={{ paddingLeft: 20, margin: "5px 0" }}>
					<li>Excel files (.xls, .xlsx, .xlsm)</li>
					<li>CSV files (.csv)</li>
				</ul>
				<p>
					💡 <strong>Tip:</strong> You can also upload your own data
					format - our system is flexible!
				</p>
			</div>
		),
		placement: "top",
	},
	{
		target: ".upload-submit-button",
		content: (
			<div>
				<h3>Step 3: Submit & Process ✅</h3>
				<p>
					After selecting your file, click the{" "}
					<strong>Upload button</strong> to submit it for processing.
				</p>
				<p>
					<strong>What happens next:</strong>
				</p>
				<ul style={{ paddingLeft: 20, margin: "10px 0" }}>
					<li>
						📧 You'll receive an email confirmation within a few
						minutes
					</li>
					<li>
						🔄 Our system automatically processes and validates your
						data
					</li>
					<li>
						📊 Your emissions data appears in the dashboard once
						complete
					</li>
					<li>
						🎯 You can immediately start setting reduction targets
					</li>
				</ul>
				<p>
					⏱️ <strong>Processing time:</strong> Usually 2-5 minutes for
					most files
				</p>
				<p>
					<strong>
						Next, let's explore how to manage your data manually!
					</strong>
				</p>
			</div>
		),
		placement: "left",
	},
	{
		target: "body",
		content: (
			<div>
				<h3>Managing Your Data 📊</h3>
				<p>
					<strong>
						Beyond file uploads, you can also add, edit, and delete
						data manually!
					</strong>
				</p>
				<p>
					Let's explore how to manage your emissions data for
					different scopes:
				</p>
				<ul style={{ paddingLeft: 20, margin: "10px 0" }}>
					<li>
						🔥 <strong>Scope 1:</strong> Direct emissions from fuels
						combusted
					</li>
					<li>
						⚡ <strong>Scope 2:</strong> Indirect emissions from
						electricity
					</li>
				</ul>
				<p>We'll show you the full CRUD operations for each!</p>
			</div>
		),
		placement: "center",
		disableBeacon: true,
	},
	{
		target: ".menu-scope1-accordion",
		content: (
			<div>
				<h3>Scope 1 Emissions 🔥</h3>
				<p>
					<strong>Scope 1</strong> covers direct emissions from
					sources you own or control, such as:
				</p>
				<ul style={{ paddingLeft: 20, margin: "10px 0" }}>
					<li>Fuel combustion in boilers and furnaces</li>
					<li>Company vehicles</li>
					<li>Fugitive emissions from gas leakage</li>
				</ul>
				<p>
					<strong>We've expanded Scope 1</strong> to show all
					available categories below!
				</p>
			</div>
		),
		placement: "right",
	},
	{
		target: ".menu-fuels-combusted-link",
		content: (
			<div>
				<h3>Fuels Combusted 🔥</h3>
				<p>
					This section tracks emissions from burning fuels like
					diesel, petrol, natural gas, and coal.
				</p>
				<p>
					<strong>
						This is where you can view and manage all your fuel
						data!
					</strong>
				</p>
			</div>
		),
		placement: "right",
	},
	{
		target: "body",
		content: (
			<div>
				<h3>Let's Visit the Fuels Combusted Page! 🔥</h3>
				<p>
					<strong>
						We're now navigating to the Fuels Combusted page...
					</strong>
				</p>
				<p>
					This page allows you to perform all CRUD operations (Create,
					Read, Update, Delete) on your fuel consumption data.
				</p>
				<p>Let's explore what you can do here!</p>
			</div>
		),
		placement: "center",
		disableBeacon: true,
	},
	{
		target: ".fuel-combusted-page-title",
		content: (
			<div>
				<h3>Fuels Combusted Management 🔥</h3>
				<p>
					<strong>Welcome to the Fuels Combusted page!</strong>
				</p>
				<p>
					Here you can manage all your fuel consumption data. This
					page demonstrates full CRUD operations:
				</p>
				<ul style={{ paddingLeft: 20, margin: "10px 0" }}>
					<li>
						<strong>Create:</strong> Add new fuel consumption
						records
					</li>
					<li>
						<strong>Read:</strong> View all your fuel data in the
						table
					</li>
					<li>
						<strong>Update:</strong> Edit existing records
					</li>
					<li>
						<strong>Delete:</strong> Remove outdated data
					</li>
				</ul>
			</div>
		),
		placement: "bottom",
	},
	{
		target: ".fuel-combusted-add-button",
		content: (
			<div>
				<h3>Create: Add New Data ➕</h3>
				<p>
					<strong>
						Click this button to add new fuel consumption!
					</strong>
				</p>
				<p>You can enter:</p>
				<ul style={{ paddingLeft: 20, margin: "10px 0" }}>
					<li>Year of consumption</li>
					<li>Facility where fuel was used</li>
					<li>Fuel type (diesel, petrol, natural gas, etc.)</li>
					<li>Amount consumed</li>
				</ul>
				<p>
					💡 <strong>Note:</strong> This feature is disabled in demo
					mode, but you can try it with your own company data!
				</p>
			</div>
		),
		placement: "bottom",
	},
	{
		target: ".fuel-combusted-table-container",
		content: (
			<div>
				<h3>Read, Update & Delete: Manage Your Data 📋</h3>
				<p>
					<strong>
						This table shows all your fuel consumption records!
					</strong>
				</p>
				<p>For each row, you can:</p>
				<ul style={{ paddingLeft: 20, margin: "10px 0" }}>
					<li>
						👁️ <strong>Read:</strong> View all details (year,
						facility, fuel type, amount)
					</li>
					<li>
						✏️ <strong>Update:</strong> Click the edit icon to
						modify any record
					</li>
					<li>
						🗑️ <strong>Delete:</strong> Click the delete icon to
						remove a record
					</li>
				</ul>
				<p>
					The table also shows when each record was created and last
					updated!
				</p>
			</div>
		),
		placement: "top",
	},
	{
		target: "body",
		content: (
			<div>
				<h3>Now Let's Check Scope 2! ⚡</h3>
				<p>Great job! You now know how to manage Scope 1 fuel data.</p>
				<p>
					<strong>
						Next, let's explore Scope 2 - Electricity management!
					</strong>
				</p>
				<p>
					Scope 2 covers indirect emissions from purchased
					electricity, steam, heating, and cooling.
				</p>
			</div>
		),
		placement: "center",
		disableBeacon: true,
	},
	{
		target: ".menu-scope2-accordion",
		content: (
			<div>
				<h3>Scope 2 Emissions ⚡</h3>
				<p>
					<strong>Scope 2</strong> covers indirect emissions from
					energy you purchase:
				</p>
				<ul style={{ paddingLeft: 20, margin: "10px 0" }}>
					<li>Electricity from the grid</li>
					<li>Renewable energy sources</li>
					<li>Non-renewable energy sources</li>
				</ul>
				<p>
					<strong>We've expanded Scope 2</strong> to show the
					electricity category below!
				</p>
			</div>
		),
		placement: "right",
	},
	{
		target: ".menu-electricity-link",
		content: (
			<div>
				<h3>Electricity ⚡</h3>
				<p>
					Track all your electricity consumption data here, including
					grid power, renewable sources, and generated electricity.
				</p>
				<p>
					<strong>
						This is where you can view and manage all your
						electricity data!
					</strong>
				</p>
			</div>
		),
		placement: "right",
	},
	{
		target: "body",
		content: (
			<div>
				<h3>Let's Visit the Electricity Page! ⚡</h3>
				<p>
					<strong>
						We're now navigating to the Electricity page...
					</strong>
				</p>
				<p>
					Just like Fuels Combusted, this page supports full CRUD
					operations for managing your electricity consumption data.
				</p>
				<p>Let's see what you can do here!</p>
			</div>
		),
		placement: "center",
		disableBeacon: true,
	},
	{
		target: ".electricity-page-title",
		content: (
			<div>
				<h3>Electricity Management ⚡</h3>
				<p>
					<strong>Welcome to the Electricity page!</strong>
				</p>
				<p>
					Just like Fuels Combusted, this page supports full CRUD
					operations for managing your electricity data:
				</p>
				<ul style={{ paddingLeft: 20, margin: "10px 0" }}>
					<li>
						<strong>Create:</strong> Add electricity consumption
						records
					</li>
					<li>
						<strong>Read:</strong> View all electricity data
					</li>
					<li>
						<strong>Update:</strong> Edit existing records
					</li>
					<li>
						<strong>Delete:</strong> Remove old data
					</li>
				</ul>
			</div>
		),
		placement: "bottom",
	},
	{
		target: ".electricity-add-button",
		content: (
			<div>
				<h3>Create: Add Electricity Data ➕</h3>
				<p>
					<strong>
						Click this button to add new electricity consumption!
					</strong>
				</p>
				<p>You can track:</p>
				<ul style={{ paddingLeft: 20, margin: "10px 0" }}>
					<li>Grid electricity (kWh)</li>
					<li>Renewable sources (solar, wind, etc.)</li>
					<li>Non-renewable sources</li>
					<li>Electricity returned to grid</li>
					<li>Costs for each category</li>
				</ul>
			</div>
		),
		placement: "bottom",
	},
	{
		target: ".electricity-table-container",
		content: (
			<div>
				<h3>Read, Update & Delete: Full Control 📊</h3>
				<p>
					<strong>
						The electricity table shows comprehensive consumption
						data!
					</strong>
				</p>
				<p>Each record displays:</p>
				<ul style={{ paddingLeft: 20, margin: "10px 0" }}>
					<li>Year and facility</li>
					<li>Grid consumption and costs</li>
					<li>Renewable and non-renewable sources</li>
					<li>Generated electricity and revenue</li>
				</ul>
				<p>
					Use the <strong>Edit</strong> and <strong>Delete</strong>{" "}
					icons to manage each record just like in Fuels Combusted!
				</p>
			</div>
		),
		placement: "top",
	},
	{
		target: ".menu-facilities",
		content: (
			<div>
				<h3>Manage Facilities</h3>
				<p>
					Add your office locations, warehouses, and production
					facilities.
				</p>
				<p>
					Each facility can have its own emissions data and targets.
				</p>
			</div>
		),
		placement: "left",
	},
	{
		target: ".reports-link",
		content: (
			<div>
				<h3>Generate Reports</h3>
				<p>Create professional reports for:</p>
				<ul style={{ paddingLeft: 20, margin: "10px 0" }}>
					<li>Board presentations</li>
					<li>Regulatory compliance</li>
					<li>Stakeholder communication</li>
				</ul>
				<p>Export as PDF, Excel, or PowerPoint.</p>
			</div>
		),
		placement: "right",
	},
	{
		target: "body",
		content: (
			<div>
				<h3>You're All Set! 🎉</h3>
				<p>You now know the basics of ARTEH. Here's what to do next:</p>
				<ol style={{ paddingLeft: 20, margin: "10px 0" }}>
					<li>Explore the Demo Company's sample data</li>
					<li>
						Switch to your own company and upload your first
						emissions data
					</li>
					<li>Set your reduction targets</li>
				</ol>
				<p>Need help? Contact support@arteh.com.au</p>
			</div>
		),
		placement: "center",
	},
];
